import { Breadcrumbs as MuiBreadCrumbs, useMediaQuery, useTheme } from '@mui/material';
import { RiArrowRightSLine } from '@remixicon/react';
import { useLocation } from '@tanstack/react-router';
import { AgText } from '../ui';
import Link from './Link';

type PageDef = {
  /**
   * Title of path that will be displayed in the breadcrumb.
   */
  title?: string;
  /**
   * Children of route, for nested breadcrumbs.
   * The key of the object is the url path.
   */
  children?: PageDefs;
  param?: PageDef | string | string[];
  id?: string;
};
type PageDefs = Record<string, string | PageDef>;

type BreadcrumbLocalization = { salesOrderDocumentPrefix?: string | null };

const pageDefs = ({ salesOrderDocumentPrefix }: BreadcrumbLocalization): PageDefs => ({
  landing: 'Home',
  'organization-settings': 'Organization Settings',
  'user-profile': 'User Profile',
  inventory: {
    title: 'Inventory',
    children: {
      'pick-tickets': { title: 'Pick Tickets', param: 'PT #{param}' },
      'receiving-tickets': { title: 'Receiving Tickets', param: 'RT #{param}' },
      'lot-tracking': { param: [null, 'Lot #{param}'] },
      repack: { title: 'Repack' },
      'production-runs': { title: 'Production Runs', param: 'PR #{param}' },
      loads: { title: 'Freight Tracking', param: 'Load #{param}' },
      transfers: { title: 'Inventory Transfers', param: 'IT #{param}' },
    },
  },
  'sales-orders': {
    title: 'Sales Orders',
    param: `${salesOrderDocumentPrefix ?? 'SO'} #{param}`,
    children: { pdf: 'PDF' },
  },
  'purchase-orders': { title: 'Purchase Orders', param: 'PO #{param}', children: { pdf: 'PDF' } },
  'grower-products': { title: 'Grower Products', param: 'GP #{param}' },
  'buy-sell-orders': { param: 'BSO #{param}' },
  contacts: {
    title: 'Contacts',
    param: { title: 'Contact #{param}', children: { edit: 'Edit Contact' } },
    children: { new: 'New Contact', view: 'View Contacts' },
  },
  settings: {
    title: 'Settings',
    children: {
      accounting: { title: 'Accounting', children: { mapping: 'Account Mappings' } },
      business: {
        title: 'Business Setup',
        children: {
          entities: { param: { title: 'Business Entity #{param}' } },
          'pricing-sheet': { title: 'Pricing Sheet', param: {} },
        },
      },
      documents: 'Documents',
      portal: 'Portal',
      inventory: {
        title: 'Inventory',
        children: {
          products: { param: { title: '#{param}' } },
          warehouses: { param: { title: 'Warehouse #{param}' } },
        },
      },
      team: 'Team',
    },
  },
  accounting: {
    children: {
      reports: { title: 'Accounting Reports' },
      invoices: { title: 'Invoices', param: '#{param}' },
      payables: { title: 'Payables', param: '#{param}' },
      bill: { id: 'bill-breadcrumb', title: 'Bills', param: '#{param}' },
      payments: { title: 'Payments', param: '#{param}', children: { 'print-checks': 'Print Checks' } },
      accounts: { title: 'Accounts', children: { register: { title: 'Register' } } },
      reconciliations: { title: 'Account Reconciliations', param: '#{param}' },
    },
  },
  reports: 'Reports',
  'business-intelligence': { title: 'Business Intelligence', children: { 'aging-reports': 'Aging Reports' } },
  'order-board': 'Order Board',
  invoices: { title: 'Invoices', param: '#{param}' },
  products: 'Products',
});

export type BreadcrumbType = { title: string; path?: string; id?: string };

export function defaultGetBreadcrumbs(paths: string[], localization: BreadcrumbLocalization = {}) {
  const breadcrumbs: BreadcrumbType[] = [];
  const atPath = [''];
  let lastPageDef: PageDef;
  let currentPageDef = pageDefs(localization);
  let paramSeqIndex = 0;

  paths.forEach((path) => {
    atPath.push(path);

    const pageDef = (currentPageDef && currentPageDef[path]) ?? lastPageDef;
    const paramDef = lastPageDef?.param;

    // When going to the home screen, there is no pageDef, but the breadcrumb still renders somehow.
    // This prevents it from crashing when that happens.
    if (!pageDef) return;

    // Some page defs don't have children, so just use the title
    if (typeof pageDef === 'string') {
      breadcrumbs.push({ title: pageDef, path: atPath.join('/') });

      return;
    }

    // Use parameterized route, if available
    if (paramDef) {
      const param = Array.isArray(paramDef) ? paramDef[paramSeqIndex] : paramDef;

      paramSeqIndex++;

      if (!param) {
        return;
      }

      const header = typeof param === 'string' ? param : param.title;

      if (header) {
        breadcrumbs.push({ title: header.replace('{param}', path), path: atPath.join('/') });
      }

      if (typeof paramDef !== 'string') {
        const nestedPageDef = paramDef as PageDef;

        if (!nestedPageDef.children) {
          return;
        }

        lastPageDef = nestedPageDef;
        currentPageDef = nestedPageDef?.children;

        return;
      }
    } else if (pageDef.title) {
      breadcrumbs.push({ title: pageDef.title, path: atPath.join('/'), id: pageDef.id });
      paramSeqIndex = 0;
    }

    lastPageDef = pageDef;
    currentPageDef = pageDef?.children;
  });

  return breadcrumbs;
}

export default function Breadcrumbs({ getBreadcrumbs }: { getBreadcrumbs?: (path: string[]) => BreadcrumbType[] }) {
  const pathname = useLocation({ select: (location) => location.pathname });
  const [_, ...paths] = pathname.split('/'); // removes leading slash
  const breadcrumbs = getBreadcrumbs ? getBreadcrumbs(paths) : defaultGetBreadcrumbs(paths);
  const theme = useTheme();
  const smallScreen = useMediaQuery(theme.breakpoints.down('md'));

  if (breadcrumbs.length <= 1) return null;

  return (
    <MuiBreadCrumbs maxItems={smallScreen ? 3 : 5} separator={<RiArrowRightSLine size="16" />}>
      {breadcrumbs.map(({ title, path, id }, i) =>
        i === breadcrumbs.length - 1 || !path ? (
          <AgText
            size="14"
            key={path}
            id={id}
            weight={i === breadcrumbs.length - 1 ? 'medium' : 'regular'}
            color={i === breadcrumbs.length - 1 ? 'primary' : 'secondary'}
          >
            {title}
          </AgText>
        ) : (
          <Link
            className="text-neutral-alpha-9"
            sx={{ textDecoration: 'none', fontSize: '14px' }}
            key={path}
            to={path}
            id={id}
            label={title}
          />
        )
      )}
    </MuiBreadCrumbs>
  );
}
